import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogBody, DialogFooter, DialogHeader } from '@/components';
import { SearchBox } from './SearchBox';

// Types for the selection picker dialog
export interface SelectionValue<T> {
  id: string;
  value: string;
  // Secondary line shown under the value, e.g. an email address
  subValue?: string;
  obj?: T;
}

export interface SelectionPickerDialogProps<T> {
  isOpen: boolean;
  onClose: () => void;
  header: string;
  subheader?: string;
  isLoading: boolean;
  values: SelectionValue<T>[];
  isUpdating: boolean;
  onSelect: (id: string, obj?: T) => void | Promise<void>;
  selectedId?: string;
  // Providing onSearchChange turns the dialog into a searchable picker
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  emptyText?: string;
}

/**
 * A reusable dialog component for selecting a value from a list of options.
 * Provides single-click selection and double-click confirmation functionality.
 * Optionally renders a search box above the options.
 */
export function SelectionPickerDialog<T>({
  isOpen,
  onClose,
  header,
  subheader,
  isLoading,
  values,
  isUpdating,
  onSelect,
  selectedId,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  emptyText,
}: SelectionPickerDialogProps<T>) {
  const { t } = useTranslation();
  const [localSelectedId, setLocalSelectedId] = useState<string | null>(null);

  // Reset local selection when dialog closes or opens
  useEffect(() => {
    if (!isOpen) {
      setLocalSelectedId(null);
      return;
    }

    // Set the initial selected value based on the prop
    setLocalSelectedId(selectedId ?? null);
  }, [isOpen, selectedId]);

  // Handle confirmation button click
  const handleConfirm = async () => {
    if (!localSelectedId) {
      return;
    }
    const selectedValue = values.find(v => v.id === localSelectedId);
    await onSelect(localSelectedId, selectedValue?.obj);
  };

  // Handle single click on option
  const handleOptionClick = (id: string) => {
    setLocalSelectedId(id);
  };

  // Handle double click on option
  const handleOptionDoubleClick = async (id: string) => {
    setLocalSelectedId(id);
    const selectedValue = values.find(v => v.id === id);
    await onSelect(id, selectedValue?.obj);
  };

  // Check if submit button should be disabled
  const isSubmitDisabled = !localSelectedId || isUpdating || isLoading;
  const isSearchable = onSearchChange !== undefined;

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <DialogHeader showClose={true}>
        {header}
      </DialogHeader>

      <DialogBody>
        {/* Optional subheader text */}
        {subheader && (
          <p className="text-sm text-muted mb-4">
            {subheader}
          </p>
        )}

        {/* Optional search box */}
        {isSearchable && (
          <div className="mb-4">
            <SearchBox
              value={searchValue ?? ''}
              onChange={onSearchChange}
              placeholder={searchPlaceholder}
              ariaLabel={searchPlaceholder}
            />
          </div>
        )}

        {/* Scrollable options container */}
        <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
          <OptionsList
            isLoading={isLoading}
            values={values}
            localSelectedId={localSelectedId}
            onOptionClick={handleOptionClick}
            onOptionDoubleClick={handleOptionDoubleClick}
            emptyText={emptyText}
          />
        </div>
      </DialogBody>

      <DialogFooter
        submitText={isUpdating ? t('common.updating') : t('common.confirm')}
        abortText={t('common.cancel')}
        submitButtonProps={{ disabled: isSubmitDisabled }}
        onSubmit={handleConfirm}
      />
    </Dialog>
  );
}


// Subcomponent for rendering loading state
const LoadingState = ({ text }: { text: string }) => (
  <div className="text-sm text-muted text-center py-4">
    {text}
  </div>
);

// Subcomponent for rendering empty state
const EmptyState = ({ text }: { text: string }) => (
  <div className="text-sm text-muted text-center py-4">
    {text}
  </div>
);

// Subcomponent for rendering selectable option
const SelectableOption = <T,>({
  item,
  isSelected,
  onClick,
  onDoubleClick
}: {
  item: SelectionValue<T>;
  isSelected: boolean;
  onClick: () => void;
  onDoubleClick: () => void;
}) => (
  <button
    key={item.id}
    type="button"
    onClick={onClick}
    onDoubleClick={onDoubleClick}
    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${isSelected
      ? 'bg-primary text-onprimary'
      : 'bg-surface-darker text-onsurface hover:bg-border'
      }`}
  >
    <span className="flex flex-col">
      <span>{item.value}</span>
      {item.subValue && (
        <span className="text-xs opacity-70">{item.subValue}</span>
      )}
    </span>
  </button>
);

// Subcomponent for rendering the options list
const OptionsList = <T,>({
  isLoading,
  values,
  localSelectedId,
  onOptionClick,
  onOptionDoubleClick,
  emptyText
}: {
  isLoading: boolean;
  values: SelectionValue<T>[];
  localSelectedId: string | null;
  onOptionClick: (id: string) => void;
  onOptionDoubleClick: (id: string) => void;
  emptyText?: string;
}) => {
  const { t } = useTranslation();

  // Show loading state
  if (isLoading) {
    return <LoadingState text={t('common.loading')} />;
  }

  // Show empty state
  if (values.length === 0) {
    return <EmptyState text={emptyText ?? t('common.noOptionsAvailable')} />;
  }

  // Render list of options
  return (
    <>
      {values.map((item) => (
        <SelectableOption
          key={item.id}
          item={item}
          isSelected={localSelectedId === item.id}
          onClick={() => onOptionClick(item.id)}
          onDoubleClick={() => onOptionDoubleClick(item.id)}
        />
      ))}
    </>
  );
};

